import { useState } from 'react';
import { Alert, Box, Button, Container, Link, Paper, Stack, TextField, Typography } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { apiClient } from '../../lib/api-client';

const schema = z.object({
  password: z
    .string()
    .min(12, 'Au moins 12 caractères')
    .regex(/[A-Z]/, 'doit contenir une majuscule')
    .regex(/[a-z]/, 'doit contenir une minuscule')
    .regex(/\d/, 'doit contenir un chiffre')
    .regex(/[^A-Za-z0-9]/, 'doit contenir un caractère spécial'),
  confirm: z.string(),
}).refine((data) => data.password === data.confirm, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['confirm'],
});

type FormValues = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const isInvitation = params.get('invitation') === '1';
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  if (!token) return <Navigate to="/forgot-password" replace />;

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post('/v1/auth/reset-password', { token, password: values.password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      setError(msg ?? 'Le lien est invalide ou expiré.');
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <Container maxWidth="sm">
      <Box component="main" sx={{ py: 8 }}>
        <Paper elevation={1} sx={{ p: 4 }}>
          <Typography component="h1" variant="h5" gutterBottom>
            {isInvitation ? 'Bienvenue — définissez votre mot de passe' : 'Nouveau mot de passe'}
          </Typography>
          {isInvitation && !done && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Votre administrateur vous a invité·e à rejoindre Premium Energy Ticketing. Choisissez un
              mot de passe pour activer votre accès.
            </Alert>
          )}

          {done ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              {isInvitation
                ? 'Compte activé. Vous allez être redirigé vers la page de connexion.'
                : 'Mot de passe mis à jour. Vous allez être redirigé vers la page de connexion.'}
            </Alert>
          ) : (
            <form onSubmit={onSubmit} noValidate>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <TextField
                  {...register('password')}
                  label="Nouveau mot de passe"
                  type="password"
                  autoComplete="new-password"
                  required
                  error={Boolean(errors.password)}
                  helperText={errors.password?.message ?? 'Au moins 12 caractères, majuscule, minuscule, chiffre, caractère spécial.'}
                />
                <TextField
                  {...register('confirm')}
                  label="Confirmation"
                  type="password"
                  autoComplete="new-password"
                  required
                  error={Boolean(errors.confirm)}
                  helperText={errors.confirm?.message}
                />
                {error && (
                  <Alert severity="error" role="alert">
                    {error}
                  </Alert>
                )}
                <Button type="submit" variant="contained" size="large" disabled={submitting}>
                  {submitting ? 'Mise à jour…' : 'Réinitialiser le mot de passe'}
                </Button>
                <Link component={RouterLink} to="/login" underline="hover">
                  Retour à la connexion
                </Link>
              </Stack>
            </form>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
